import * as React from 'react'

import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter'
import DrillDownFilter, {
  decodeDrillDownFilterSelectionId,
  encodeDrillDownFilterSelectionId,
} from '~/client/src/shared/components/DrillDownFilter'
import { FilterButton } from '~/client/src/shared/components/Filters/triggers'
import { ResetIcon } from '~/client/src/shared/components/icons/ResetIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { isUnassignedFilterOptionId } from '~/client/src/shared/hooks/useFilters/filterOptionIds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { compareLabelsAlphanumeric } from '~/client/src/shared/utils/compareGroupsByLabel'

import { ISourceMap, TableFiltersProps } from './types'

interface IDrillDownFilterItem {
  id: string
  optionId: string
  label: string
  totalCount: number
  visibleCount: number
}

const getDefaultOptionName = (optionId: string) => {
  return isUnassignedFilterOptionId(optionId)
    ? Localization.translator.unassigned
    : optionId
}

const sortOptionIds = (
  optionIds: string[],
  getLabel: (optionId: string) => string,
) => {
  return optionIds.sort((a, b) => {
    if (isUnassignedFilterOptionId(a)) return 1
    if (isUnassignedFilterOptionId(b)) return -1
    return compareLabelsAlphanumeric(getLabel(a), getLabel(b))
  })
}

const getAllowedIdsForFacet = (
  facetKey: string,
  facets: TableFiltersProps['facets'],
  appliedFilters: TableFiltersProps['appliedFilters'],
  sourceMapByFacet: { [facetKey: string]: ISourceMap },
  availableRecords: Array<{ id: string }>,
) => {
  let filtered = availableRecords

  for (const facet of facets) {
    if (facet.key === facetKey) continue

    const applied = appliedFilters[facet.key]
    if (!applied || applied.size === 0) continue

    const allowedIds = new Set<string>()
    applied.forEach(optionId => {
      const recordIds = sourceMapByFacet[facet.key]?.[optionId] || []
      recordIds.forEach(id => allowedIds.add(id))
    })

    filtered = filtered.filter(record => allowedIds.has(record.id))
  }

  return new Set(filtered.map(record => record.id))
}

const TableFilters: React.FC<TableFiltersProps> = ({
  facets,
  sourceMapByFacet,
  appliedFilters,
  onFilterApply,
  onResetAll,
  availableRecords,
  getOptionName,
  children,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const visibleFacets = React.useMemo(
    () => facets.filter(facet => !facet.isHiddenInFilterUI),
    [facets],
  )

  const initialSelection = React.useMemo(() => {
    const ids: string[] = []

    visibleFacets.forEach(({ key }) => {
      appliedFilters[key]?.forEach(optionId => {
        ids.push(encodeDrillDownFilterSelectionId(key, optionId))
      })
    })

    return ids
  }, [visibleFacets, appliedFilters])

  const activeCount = React.useMemo(() => {
    return Object.values(appliedFilters).reduce(
      (count, selected) => count + selected.size,
      0,
    )
  }, [appliedFilters])

  const drillDownCategories =
    React.useMemo((): IDrillDownFilterCategory<IDrillDownFilterItem>[] => {
      return visibleFacets
        .map(facet => {
          const getLabel = (optionId: string) => {
            if (getOptionName) {
              return getOptionName(optionId, facet.key)
            }
            if (facet.displayFormatter) {
              return facet.displayFormatter(optionId)
            }
            return getDefaultOptionName(optionId)
          }

          const visibleIds = getAllowedIdsForFacet(
            facet.key,
            facets,
            appliedFilters,
            sourceMapByFacet,
            availableRecords,
          )

          const items = sortOptionIds(
            Object.keys(sourceMapByFacet[facet.key] || {}),
            getLabel,
          ).map(optionId => {
            const recordIds = sourceMapByFacet[facet.key]?.[optionId] || []
            const visibleCount = recordIds.filter(id =>
              visibleIds.has(id),
            ).length

            return {
              id: encodeDrillDownFilterSelectionId(facet.key, optionId),
              optionId,
              label: getLabel(optionId),
              totalCount: recordIds.length,
              visibleCount,
            }
          })

          const renderItem = (
            item: IDrillDownFilterItem,
            _isSelected: boolean,
            label: string,
          ) => (
            <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:gap-3 tw:min-w-0">
              <span className="tw:min-w-0 tw:flex-1 tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
                {label}
              </span>
              <span className="tw:shrink-0 tw:text-body-sm tw:text-muted-foreground">
                {item.visibleCount === item.totalCount
                  ? item.totalCount
                  : `${item.visibleCount}/${item.totalCount}`}
              </span>
            </div>
          )

          const base = {
            id: facet.key,
            label: facet.label,
            items,
            getItemId: (item: IDrillDownFilterItem) => item.id,
            getItemLabel: (item: IDrillDownFilterItem) => item.label,
            renderItem,
          }

          const hierarchy = facet.hierarchy
          if (!hierarchy) {
            return base
          }

          return {
            ...base,
            kind: 'tree' as const,
            getItemParentId: (item: IDrillDownFilterItem) => {
              const parent = hierarchy.getParentValue(item.optionId)
              return parent
                ? encodeDrillDownFilterSelectionId(facet.key, parent)
                : null
            },
            getItemNodeLabel: (item: IDrillDownFilterItem) =>
              hierarchy.getOwnLabel(item.optionId) || item.label,
          }
        })
        .filter(category => category.items.length > 0)
        .sort((a, b) => compareLabelsAlphanumeric(a.label, b.label))
    }, [
      facets,
      visibleFacets,
      sourceMapByFacet,
      appliedFilters,
      availableRecords,
      getOptionName,
    ])

  const initialCategoryId =
    drillDownCategories.length === 1 ? drillDownCategories[0].id : undefined

  const handleApply = React.useCallback(
    (selectedIds: string[]) => {
      const selectedByFacet: Record<string, string[]> = {}

      selectedIds.forEach(id => {
        const decoded = decodeDrillDownFilterSelectionId(id)
        if (!decoded) return

        if (!selectedByFacet[decoded.facetKey]) {
          selectedByFacet[decoded.facetKey] = []
        }

        selectedByFacet[decoded.facetKey].push(decoded.optionId)
      })

      visibleFacets.forEach(({ key }) => {
        onFilterApply(key, selectedByFacet[key] || [])
      })
    },
    [visibleFacets, onFilterApply],
  )

  return (
    <div className="tw:relative tw:flex tw:items-center tw:h-full tw:grow-0 tw:gap-2">
      <DrillDownFilter.Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        trigger={
          <FilterButton
            label={Localization.translator.filter}
            badgeCount={activeCount}
            disabled={drillDownCategories.length === 0}
          />
        }
      >
        <DrillDownFilter.Desktop
          categories={drillDownCategories}
          initialSelection={initialSelection}
          initialCategoryId={initialCategoryId}
          onApply={handleApply}
        />
      </DrillDownFilter.Popover>
      {children}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onResetAll}
        title={Localization.translator.resetFilters}
      >
        <ResetIcon size="xs" />
      </Button>
    </div>
  )
}

export default TableFilters
